/** La Quiniela JA -- lógica PURA de presentación (sin red). No es un motor: solo combina las probabilidades
 * 1/X/2 que ya publicó JA para estimar el boleto que arma el usuario, con la misma regla que el optimizador
 * del backend (probabilidad de acertar los 14 = producto de la cobertura de cada partido; columnas = producto
 * de signos marcados; coste = columnas x precio). */
import { GOAL_CATS, SIGNS, type GoalCat, type PlenoView, type QuinielaMatchView, type Sign } from '@/types/quiniela'

export function isPleno(p: PlenoView | { error: string } | null | undefined): p is PlenoView {
  return !!p && !('error' in p && p.error) && 'home' in p
}

export function kindOf(n: number): 'simple' | 'doble' | 'triple' | null {
  return n === 1 ? 'simple' : n === 2 ? 'doble' : n === 3 ? 'triple' : null
}

/** "2 dobles · 1 triple": plural correcto para el resumen de un boleto. */
export function kindsSummary(dobles: number, triples: number): string {
  return `${dobles} ${dobles === 1 ? 'doble' : 'dobles'} · ${triples} ${triples === 1 ? 'triple' : 'triples'}`
}

export const KIND_LABEL = { simple: 'Simple', doble: 'Doble', triple: 'Triple' } as const

/** Signos de un partido ordenados de mayor a menor probabilidad (desempate 1, X, 2). */
export function rankSigns(p: Record<Sign, number>): Sign[] {
  return [...SIGNS].sort((a, b) => p[b] - p[a] || SIGNS.indexOf(a) - SIGNS.indexOf(b))
}

export function normalize(p: Record<Sign, number>): Record<Sign, number> {
  const t = SIGNS.reduce((s, k) => s + Math.max(0, p[k]), 0) || 1
  return { '1': Math.max(0, p['1']) / t, X: Math.max(0, p.X) / t, '2': Math.max(0, p['2']) / t }
}

export type Openness = 'clear' | 'open' | 'very_open'

/** Qué tan abierto es un partido: diferencia entre las dos opciones más probables. Explica visualmente por qué
 * JA coloca un doble/triple (describe el partido, no la confianza de JA). */
export function openness(p: Record<Sign, number>): Openness {
  const [a, b] = rankSigns(p)
  const margin = p[a] - p[b]
  if (margin < 0.06 && p[a] < 0.42) return 'very_open'
  if (margin < 0.12) return 'open'
  return 'clear'
}

export const OPENNESS_TEXT: Record<Openness, string> = {
  clear: 'Favorito claro',
  open: 'Partido abierto',
  very_open: 'Muy igualado',
}

export function coverageOf(p: Record<Sign, number>, signs: Sign[]): number {
  return signs.reduce((s, k) => s + p[k], 0)
}

export interface TicketEstimate {
  complete: boolean
  columns: number
  cost: number
  dobles: number
  triples: number
  /** Probabilidad de acertar los 14 resultados; `null` si falta algún dato o partido. */
  probability: number | null
  /** Media geométrica de la cobertura por partido (0-1): "qué tan bien cubre cada partido". */
  perMatchCoverage: number | null
  missingData: number[]
}

export function estimateTicket(
  matches: QuinielaMatchView[], picks: Record<number, Sign[]>, price: number,
): TicketEstimate {
  const regular = matches.filter((m) => m.position <= 14)
  let columns = 1, dobles = 0, triples = 0, complete = true
  let logP = 0
  let unknown = false
  const missingData: number[] = []
  for (const m of regular) {
    const signs = picks[m.position] ?? []
    if (signs.length === 0) { complete = false; continue }
    columns *= signs.length
    if (signs.length === 2) dobles++
    if (signs.length === 3) triples++
    if (signs.length === 3) continue // cobertura 1
    if (!m.probabilities) { unknown = true; missingData.push(m.position); continue }
    logP += Math.log(coverageOf(normalize(m.probabilities), signs))
  }
  const probability = complete && !unknown ? Math.exp(logP) : null
  return {
    complete, columns, cost: Math.round(columns * price * 100) / 100, dobles, triples, probability,
    perMatchCoverage: probability != null ? Math.exp(logP / regular.length) : null, missingData,
  }
}

/** Pleno: los goles de cada equipo son Poisson independientes, así que P(local=a, visitante=b) es el producto
 * de las marginales que ya publica JA. */
export function plenoProbability(p: PlenoView, home: GoalCat, away: GoalCat): number {
  return (p.home_marginal[home] ?? 0) * (p.away_marginal[away] ?? 0)
}

export function jaSingleColumn(matches: QuinielaMatchView[]): Record<number, Sign[]> {
  const out: Record<number, Sign[]> = {}
  for (const m of matches) if (m.position <= 14 && m.probabilities) out[m.position] = [rankSigns(m.probabilities)[0]]
  return out
}

const nf = (digits: number) => new Intl.NumberFormat('es-ES', { maximumFractionDigits: digits })

/** Probabilidad de un signo: porcentaje entero ("35 %"). */
export function formatSignPct(v: number): string {
  return `${nf(0).format(v * 100)} %`
}

/** Probabilidad del BOLETO completo: nunca "0,00 %". Titular "1 entre N" y porcentaje con cifras significativas. */
export function formatOneIn(p: number | null | undefined): string {
  if (p == null || p <= 0) return '—'
  const n = Math.round(1 / p)
  return n <= 1 ? 'Casi seguro' : `1 entre ${new Intl.NumberFormat('es-ES').format(n)}`
}

export function formatTicketPct(p: number | null | undefined): string {
  if (p == null || p <= 0) return '—'
  const pct = p * 100
  if (pct >= 1) return `${nf(2).format(pct)} %`
  return `${new Intl.NumberFormat('es-ES', { maximumSignificantDigits: 2 }).format(pct)} %`
}

export function formatEur(v: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v)
}

export function parseBudget(raw: string): number | null {
  const n = Number(raw.replace(',', '.').replace(/[^0-9.]/g, ''))
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null
}

export function ticketToText(
  matches: QuinielaMatchView[], picks: Record<number, Sign[]>, pleno: { home: GoalCat; away: GoalCat } | null,
): string {
  const lines = matches.filter((m) => m.position <= 14).map((m) =>
    `${m.position}. ${m.home_name} - ${m.away_name}: ${(picks[m.position] ?? []).join('') || '—'}`)
  const p15 = matches.find((m) => m.position === 15)
  if (p15 && pleno) lines.push(`15. ${p15.home_name} - ${p15.away_name}: Pleno ${pleno.home} - ${pleno.away}`)
  return lines.join('\n')
}

export { GOAL_CATS }
